"use client"

import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { z } from "zod"
import axios from "axios"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { API_BASE_URL } from "@/lib/api"

// Zod schema for registration validation
const registrationSchema = z
  .object({
    userId: z.string().min(2, "User ID must be at least 2 characters"),
    name: z.string().min(2, "Name must be at least 2 characters"),
    pin: z.string().min(4, "PIN must be at least 4 digits").regex(/^\d+$/, "PIN must contain only numbers"),
    confirmPin: z.string(),
    referralCode: z.string().optional()
  })
  .refine((data) => data.pin === data.confirmPin, {
    message: "PINs don't match",
    path: ["confirmPin"],
  })

// API base URL
// const API_BASE_URL = "https://api-dear-lottery.threecircle5.com/api"
// Axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
})

// Basic input sanitizer
function sanitizeInput(str: string) {
  return str.replace(/['";\-]/g, "")
}

// Types for API response
interface User {
  id: string
  userId: string
  pinHash: string
  name: string
  balance: number
  role: string
  isActive: boolean
  createdAt: string
  updatedAt: string
  referralCode: string
  referredBy?: string
}

interface ApiResponse {
  success: boolean
  user: User
}

export default function RegisterPage() {
  const [form, setForm] = useState({
    userId: "",
    name: "",
    pin: "",
    confirmPin: "",
    referralCode: "",
    role: "USER", // Automatically set to USER
  })

  const [errors, setErrors] = useState<{ [key: string]: string }>({})
  const [isPending, setIsPending] = useState(false)
  const [submitError, setSubmitError] = useState("")
  const [successMessage, setSuccessMessage] = useState("")
  const navigate = useNavigate()

  // Handle input change
  function handleChange(e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) {
    const { name, value } = e.target
    
    // Convert referral code to uppercase automatically
    if (name === 'referralCode') {
      setForm({ ...form, [name]: value.toUpperCase() })
    } else {
      setForm({ ...form, [name]: value })
    }
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: "" }))
    }
  }

  // Form submit handler
  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setSubmitError("")
    setSuccessMessage("")
    setIsPending(true)
    setErrors({})

    // Sanitize inputs
    const sanitizedForm = {
      userId: sanitizeInput(form.userId.trim()),
      name: sanitizeInput(form.name.trim()),
      pin: form.pin, // Don't sanitize PIN as it should only contain numbers
      confirmPin: form.confirmPin,
      referralCode: form.referralCode ? sanitizeInput(form.referralCode.trim()) : undefined,
      role: "USER", // Always set to USER role
    }

    // Validate with Zod
    const result = registrationSchema.safeParse(sanitizedForm)
    if (!result.success) {
      // Format errors
      const fieldErrors: { [key: string]: string } = {}
      result.error.errors.forEach((err) => {
        if (err.path.length > 0) {
          fieldErrors[err.path[0]] = err.message
        }
      })
      setErrors(fieldErrors)
      setIsPending(false)
      return
    }

    try {
      // Prepare data for API (exclude confirmPin)
      const { confirmPin, ...apiData } = result.data

      // Make API call
      const response = await api.post<ApiResponse>("/users/register", apiData)
      
      if (response.data.success) {
        setSuccessMessage("Registration successful! Redirecting to login...")
        
        setTimeout(() => {
          navigate("/DearLottery")
        }, 2000)
      } else {
        setSubmitError("Registration failed. Please try again.")
      }
    } catch (error: any) {
      console.error("Registration error:", error)
      
      // Handle different types of errors
      if (axios.isAxiosError(error)) {
        if (error.response) {
          // Server responded with error status
          const serverError = error.response.data
          if (serverError.error === "Refer Code invalid!") {
            setSubmitError("Invalid referral code. Please check and try again.")
          } else {
            setSubmitError(serverError.error || "Registration failed. Please try again.")
          }
        } else if (error.request) {
          // Request was made but no response received
          setSubmitError("Network error. Please check your connection and try again.")
        } else {
          // Something else happened
          setSubmitError("An unexpected error occurred. Please try again.")
        }
      } else {
        // Non-Axios error
        setSubmitError("An unexpected error occurred. Please try again.")
      }
    } finally {
      setIsPending(false)
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-[#2a003a] to-[#1a002a] px-4 py-8 relative overflow-hidden">
      {/* Background glow effects */}
      <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-purple-600 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob"></div>
      <div className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-pink-600 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-2000"></div>

      <Card className="w-full max-w-md bg-white/10 backdrop-blur-lg border border-white/20 rounded-xl shadow-2xl text-white relative z-10">
        <CardHeader className="text-center pt-8 pb-6">
          <img
            src="../assets/logo.jpg"
            alt="Three Circle 7 Logo"
            className="mx-auto h-16 w-16 rounded-full mb-4 shadow-lg border border-white/20"
          />
          <CardTitle className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-red-400 to-green-400">
            Register to Three Circle 7
          </CardTitle>
          <CardDescription className="text-white/80 mt-2">
            Fill the form below to create your account.
          </CardDescription>
        </CardHeader>

        <CardContent className="pb-8">
          <form onSubmit={handleSubmit} className="space-y-6" noValidate>
            {/* User ID */}
            <div className="grid gap-2">
              <Label htmlFor="userId" className="text-white/90">
                User ID
              </Label>
              <Input
                id="userId"
                name="userId"
                type="text"
                placeholder="Enter your user ID"
                required
                value={form.userId}
                onChange={handleChange}
                className="bg-white/10 border border-white/30 text-white placeholder:text-white/60 focus:ring-purple-500 focus:border-purple-500"
              />
              {errors.userId && (
                <p className="text-red-400 text-sm mt-1">{errors.userId}</p>
              )}
            </div>

            {/* Name */}
            <div className="grid gap-2">
              <Label htmlFor="name" className="text-white/90">
                Name
              </Label>
              <Input
                id="name"
                name="name"
                type="text"
                placeholder="Your full name"
                required
                value={form.name}
                onChange={handleChange}
                className="bg-white/10 border border-white/30 text-white placeholder:text-white/60 focus:ring-purple-500 focus:border-purple-500"
              />
              {errors.name && (
                <p className="text-red-400 text-sm mt-1">{errors.name}</p>
              )}
            </div>
           
            {/* PIN */}
            <div className="grid gap-2">
              <Label htmlFor="pin" className="text-white/90">
                PIN
              </Label>
              <Input
                id="pin"
                name="pin"
                type="password"
                placeholder="Enter 4-digit PIN"
                required
                maxLength={4}
                value={form.pin}
                onChange={handleChange}
                className="bg-white/10 border border-white/30 text-white placeholder:text-white/60 focus:ring-purple-500 focus:border-purple-500"
              />
              {errors.pin && (
                <p className="text-red-400 text-sm mt-1">{errors.pin}</p>
              )}
            </div>

            {/* Confirm PIN */}
            <div className="grid gap-2">
              <Label htmlFor="confirmPin" className="text-white/90">
                Confirm PIN
              </Label>
              <Input
                id="confirmPin"
                name="confirmPin"
                type="password"
                placeholder="Confirm your PIN"
                required
                maxLength={4}
                value={form.confirmPin}
                onChange={handleChange}
                className="bg-white/10 border border-white/30 text-white placeholder:text-white/60 focus:ring-purple-500 focus:border-purple-500"
              />
              {errors.confirmPin && (
                <p className="text-red-400 text-sm mt-1">{errors.confirmPin}</p>
              )}
            </div>

            {/* Referral Code (Optional) */}
            <div className="grid gap-2">
              <Label htmlFor="referralCode" className="text-white/90">
                Referral Code (Optional)
              </Label>
              <Input
                id="referralCode"
                name="referralCode"
                type="text"
                placeholder="Enter referral code if any"
                value={form.referralCode}
                onChange={handleChange}
                className="bg-white/10 border border-white/30 text-white placeholder:text-white/60 focus:ring-purple-500 focus:border-purple-500 uppercase"
                maxLength={6}
              />
              <p className="text-white/60 text-xs">
                If you were referred by someone, enter their referral code here
              </p>
            </div>

            {successMessage && (
              <p className="text-green-400 text-center text-sm">{successMessage}</p>
            )}

            {submitError && (
              <p className="text-red-400 text-center text-sm">{submitError}</p>
            )}

            <Button
              type="submit"
              className="w-full bg-gradient-to-r from-red-500 to-green-600 hover:from-pink-600 hover:to-purple-700 text-white font-semibold py-3 rounded-lg shadow-lg transition-all duration-300"
              disabled={isPending}
            >
              {isPending ? "Registering..." : "Register"}
            </Button>

            {/* Login link */}
            <p className="text-center text-white/70 text-sm mt-4">
              Already have an account?{" "}
              <button
                type="button"
                onClick={() => navigate("/DearLottery")}
                className="text-green-400 hover:text-green-600 font-semibold underline underline-offset-2 cursor-pointer"
              >
                Login here.
              </button>
            </p>
          </form>

          <p className="text-center text-white/60 text-sm mt-8">
            {"© 2025 Three Circle 7. All rights reserved."}
          </p>
        </CardContent>
      </Card>
    </div>
  )
}